import { assertNever } from './assert-never.js';
import { getAt } from './object.js';
const isOtherText = (value, brand) => {
    return brand.toLowerCase() === 'blocket' ? value.toLowerCase() === 'övriga' : value.toLowerCase() === 'andre';
};
/**
 * Creates a title by combining make and model text, filtering out falsy values
 * Returns undefined if both make and model are falsy
 * If both make and model are present, will filter out "Andre"/"Övriga" values
 */
const joinMakeAndModel = (brand, make, model) => {
    const values = [make, model].filter((value) => Boolean(value));
    return values.length > 0
        ? values
            .filter((value) => values.length === 1 || !isOtherText(value, brand))
            .join(' ')
            .replace(/\s{2,}/g, ' ')
            .trim()
        : undefined;
};
/**
 * Finds make and model information from item data and lookup
 */
const findMakeAndModelText = (brand, item, adInputLookup) => (makeKey, modelKey) => {
    const makeId = getAt(item, makeKey);
    const lookupKey = makeKey.replace('.', '.children.');
    const davsValues = getAt(adInputLookup, lookupKey)?.davs?.values;
    const make = davsValues?.find((value) => value.id === makeId || ('code' in value && value.code === makeId));
    const model = getAt(item, modelKey ?? '');
    return make?.text ? joinMakeAndModel(brand, make.text, model) : undefined;
};
export const generateTitle = (brand, adModel, adInputLookup, item) => {
    if (!item)
        return;
    const extractMakeAndModel = findMakeAndModelText(brand, item, adInputLookup);
    switch (adModel) {
        case 'boat-dock':
        case 'boat-motor':
        case 'boat-rent':
        case 'boat-wanted':
        case 'boat-sale':
        case 'boat-new-sale':
        case 'agriculture-tools':
            return undefined; // all above models allow users to set title
        case 'car-leasing':
        case 'car-leasing-business':
        case 'car-new':
        case 'car-used':
        case 'car-used-business':
        case 'a-tractor': {
            // Special handling for cars since make and model is stored in a single field
            const modelAndMakeId = item?.model_and_make;
            const davsValues = adInputLookup.model_and_make.davs?.values;
            const modelData = davsValues?.find((value) => value.id === modelAndMakeId);
            const make = davsValues?.find((value) => modelData && 'parentId' in modelData ? value.id === modelData?.parentId : undefined);
            return joinMakeAndModel(brand, make?.text, modelData?.text);
        }
        case 'motorcycle':
        case 'atv':
        case 'snowmobile':
        case 'moped': {
            return extractMakeAndModel('make', 'model');
        }
        case 'caravan': {
            return extractMakeAndModel('caravan_model.make', 'caravan_model.model');
        }
        case 'mobile-home': {
            return extractMakeAndModel('mobile_home_model.make', 'mobile_home_model.model');
        }
        case 'agriculture-thresher': {
            return extractMakeAndModel('thresher_model.make', 'thresher_model.model');
        }
        case 'agriculture-tractor': {
            return extractMakeAndModel('tractor_model.make', 'tractor_model.model');
        }
        case 'bus': {
            return extractMakeAndModel('bus_model.make', 'bus_model.model');
        }
        case 'truck': {
            return extractMakeAndModel('truck_model.make', 'truck_model.model');
        }
        case 'construction': {
            return extractMakeAndModel('construction_model.make', 'construction_model.model');
        }
        default:
            assertNever(adModel);
    }
};
